import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, isImpersonating, getImpersonationDetails } from "@/App";
import { useBranding } from "@/contexts/BrandingContext";
import { usePresentationMode } from "@/contexts/PresentationModeContext";
import {
  LayoutDashboard,
  Globe,
  Target,
  Handshake,
  TrendingUp,
  Linkedin,
  MousePointerClick,
  Megaphone,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Users,
  Building,
  Building2,
  ArrowLeftRight,
  Monitor,
  Shield,
  EyeOff,
  Eye,
  Brain,
  MessageSquare,
  Activity,
  BarChart3,
  Search,
  FileBarChart,
  Database,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

import axios from "axios";
import { API } from "@/App";

// JCI Platform Logo URL
const JMAP_LOGO_URL = "/jci-icon.svg";

// Nav items with page IDs matching backend
const navItems = [
  { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard", pageId: "dashboard" },
  { to: "/app/website-traffic", icon: Globe, label: "Website Traffic", pageId: "website-traffic" },
  { to: "/app/social-insights", icon: Linkedin, label: "Social Insights", pageId: "social-insights" },
  { to: "/app/form-submissions", icon: MousePointerClick, label: "Form Submissions", pageId: "form-submissions" },
  { to: "/app/mqls", icon: Target, label: "MQLs", pageId: "mqls" },
  { to: "/app/sqls", icon: Handshake, label: "SQLs", pageId: "sqls" },
  { to: "/app/deals", icon: TrendingUp, label: "Deals Pipeline", pageId: "deals" },
  { to: "/app/google-ads", icon: BarChart3, label: "Google Ads", pageId: "google-ads" },
  { to: "/app/for-marketing-insights", icon: Monitor, label: "For Marketing Insights", pageId: "for-marketing-insights" },
  { to: "/app/goals", icon: Target, label: "Marketing Goals", pageId: "goals" },
  { to: "/app/reports", icon: FileBarChart, label: "Reports", pageId: "reports" },
  { to: "/app/anomaly-detection", icon: Activity, label: "Anomaly Detection", pageId: "anomaly-detection" },
  { to: "/app/ai-chat", icon: MessageSquare, label: "AI Chat", pageId: "ai-chat" },
  { to: "/app/lead-scoring", icon: Brain, label: "Lead Scoring", pageId: "lead-scoring" },
  { to: "/app/announcements", icon: Megaphone, label: "Announcements", pageId: "announcements" },
  { to: "/app/unified-reporting", icon: Database, label: "Unified Reporting Agent", pageId: "unified-reporting" },
  { to: "/app/linkedin-abm-agent", icon: Target, label: "LinkedIn ABM Agent", pageId: "linkedin-abm-agent" },
  { to: "/app/google-ads-api", icon: BarChart3, label: "Google Ads API (Live)", pageId: "google-ads-api", adminOnly: true },
];

const adminNavItems = [
  { to: "/app/admin", icon: Settings, label: "Settings" },
];

const superAdminNavItems = [
  { to: "/app/super-admin", icon: Building2, label: "Tenant Management" },
  { to: "/app/admin-panel", icon: Shield, label: "Platform Owner Panel" },
];

const SidebarLayout = () => {
  const { user, logout, isAdmin, isSuperAdmin, sessionToken } = useAuth();
  const { pageTitles: brandingPageTitles, platformName } = useBranding();
  const { isPresentationMode, togglePresentationMode } = usePresentationMode();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [visiblePages, setVisiblePages] = useState(null); // null = loading, array = loaded
  const [gscConnected, setGscConnected] = useState(false); // Track if GSC is connected for this tenant
  
  // Check if we're impersonating - grant admin access to the impersonator
  const impersonating = isImpersonating();
  const effectiveIsAdmin = isAdmin || impersonating;  // Show admin section when impersonating
  const [homeTenantId, setHomeTenantId] = useState(null); // Dynamically fetched, no hardcoding
  
  // Read tenant context fresh (same pattern as working MQL page)
  const viewingTenantId = sessionStorage.getItem("viewing_tenant_id");
  const viewingTenantName = sessionStorage.getItem("viewing_tenant_name");
  
  // Determine if viewing a non-home tenant (for showing the "exit" banner)
  // Compare against dynamically fetched home tenant, not hardcoded ID
  const isViewingNonHomeTenant = homeTenantId && viewingTenantId && viewingTenantId !== homeTenantId;
  
  // Custom page titles - use branding context as primary source (always fresh from backend)
  // Fall back to local state for super admin tenant-specific overrides
  const [localPageTitles, setLocalPageTitles] = useState({});
  const pageTitles = Object.keys(brandingPageTitles).length > 0 ? brandingPageTitles : localPageTitles;

  // Fetch home tenant for super admins OR set tenant context for regular users
  useEffect(() => {
    const setupTenantContext = async () => {
      if (!sessionToken) return;
      
      if (isSuperAdmin) {
        // Super admins: fetch their default home tenant
        try {
          const response = await axios.get(`${API}/tenants/default-home`, {
            params: { Authorization: `Bearer ${sessionToken}` }
          });
          if (response.data?.tenant_id) {
            setHomeTenantId(response.data.tenant_id);
          }
        } catch (err) {
          console.error("Failed to fetch home tenant:", err);
        }
      } else {
        // Regular users: set their tenant context from /auth/me if not already set
        const existingTenantId = sessionStorage.getItem("viewing_tenant_id");
        if (!existingTenantId) {
          try {
            const response = await axios.get(`${API}/auth/me`, {
              params: { Authorization: `Bearer ${sessionToken}` }
            });
            // Get tenant_id from user's memberships
            const memberships = response.data?.tenant_memberships || [];
            if (memberships.length > 0) {
              const firstMembership = memberships[0];
              const tenantId = typeof firstMembership === 'object' ? firstMembership.tenant_id : firstMembership;
              if (tenantId) {
                console.log("Setting tenant context for regular user:", tenantId);
                sessionStorage.setItem("viewing_tenant_id", tenantId);
                // Dispatch event to trigger page visibility fetch
                window.dispatchEvent(new Event("tenant-changed"));
              }
            }
          } catch (err) {
            console.error("Failed to get user tenant context:", err);
          }
        }
      }
    };
    setupTenantContext();
  }, [sessionToken, isSuperAdmin]);

  const exitTenantView = async () => {
    // Go back to home tenant - fetch it dynamically
    try {
      const response = await axios.get(`${API}/tenants/default-home`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      if (response.data?.tenant_id) {
        sessionStorage.setItem("viewing_tenant_id", response.data.tenant_id);
        sessionStorage.setItem("viewing_tenant_name", response.data.name || "Home");
      } else {
        sessionStorage.removeItem("viewing_tenant_id");
        sessionStorage.removeItem("viewing_tenant_name");
      }
    } catch (err) {
      console.error("Failed to fetch home tenant:", err);
      sessionStorage.removeItem("viewing_tenant_id");
      sessionStorage.removeItem("viewing_tenant_name");
    }
    
    // Dispatch tenant-changed event for branding context to update
    window.dispatchEvent(new Event("tenant-changed"));
    
    window.location.href = "/app/dashboard";
  };

  // Track tenant changes to re-fetch visible pages
  const [tenantChangeCount, setTenantChangeCount] = useState(0);

  // Listen for tenant-changed events (dispatched by App.js when user's tenant is set)
  useEffect(() => {
    const handleTenantChange = () => {
      console.log("tenant-changed event received, triggering visible pages refresh");
      setTenantChangeCount(prev => prev + 1);
    };
    
    window.addEventListener("tenant-changed", handleTenantChange);
    return () => window.removeEventListener("tenant-changed", handleTenantChange);
  }, []);

  // Fetch visible pages and page titles for current tenant context
  useEffect(() => {
    const fetchVisiblePages = async () => {
      if (!sessionToken) return;
      
      // Read tenant ID fresh from sessionStorage (same pattern as working MQL page)
      let currentTenantId = sessionStorage.getItem("viewing_tenant_id");
      
      // For regular users without tenant ID, try to get it from /auth/me
      if (!currentTenantId && !isSuperAdmin) {
        console.log("No tenant ID for regular user, fetching from /auth/me");
        try {
          const meResponse = await axios.get(`${API}/auth/me`, {
            params: { Authorization: `Bearer ${sessionToken}` }
          });
          const memberships = meResponse.data?.tenant_memberships || [];
          if (memberships.length > 0) {
            const firstMembership = memberships[0];
            currentTenantId = typeof firstMembership === 'object' ? firstMembership.tenant_id : firstMembership;
            if (currentTenantId) {
              sessionStorage.setItem("viewing_tenant_id", currentTenantId);
            }
          }
        } catch (err) {
          console.error("Failed to get tenant from /auth/me:", err);
        }
      }
      
      console.log("=== fetchVisiblePages called ===");
      console.log("sessionToken exists:", !!sessionToken);
      console.log("currentTenantId:", currentTenantId);
      console.log("isSuperAdmin:", isSuperAdmin);
      
      try {
        // Super admins viewing a specific tenant get that tenant's page visibility
        if (currentTenantId && isSuperAdmin) {
          console.log("Super admin fetching page visibility for tenant:", currentTenantId);
          const [visibilityResponse, settingsResponse] = await Promise.all([
            axios.get(
              `${API}/tenants/${currentTenantId}/page-visibility`,
              { params: { Authorization: `Bearer ${sessionToken}`, _t: Date.now() } }
            ),
            axios.get(
              `${API}/tenants/${currentTenantId}/display-settings`,
              { params: { Authorization: `Bearer ${sessionToken}`, _t: Date.now() } }
            ).catch(() => ({ data: {} }))
          ]);
          console.log("Page visibility response:", visibilityResponse.data);
          // If visible_pages is empty, show all pages (default behavior)
          const fetchedPages = visibilityResponse.data.visible_pages || [];
          if (fetchedPages.length === 0) {
            console.log("No visible pages configured, showing all pages");
            setVisiblePages(null); // null means show all
          } else {
            setVisiblePages(fetchedPages);
          }
          // Check both paths for page_titles
          const fetchedPageTitles = settingsResponse.data?.page_titles 
            || settingsResponse.data?.display_settings?.page_titles 
            || {};
          console.log("Fetched page titles for super admin:", fetchedPageTitles);
          setLocalPageTitles(fetchedPageTitles);
        } else {
          // Regular users get their allowed pages from my-visible-pages endpoint
          // Pass X-Tenant-ID so the endpoint uses the correct tenant context
          console.log("Regular user fetching my-visible-pages endpoint, tenant:", currentTenantId);
          const response = await axios.get(
            `${API}/tenants/my-visible-pages`,
            { params: { 
              Authorization: `Bearer ${sessionToken}`, 
              "X-Tenant-ID": currentTenantId,
              _t: Date.now() 
            } }
          );
          console.log("My visible pages response:", response.data);
          console.log("Setting visiblePages to:", response.data.visible_pages);
          // Use the exact visible_pages from the backend
          const fetchedPages = response.data.visible_pages || [];
          // Always set visible pages to what the backend returns
          // Empty array means show only dashboard (most restrictive)
          setVisiblePages(fetchedPages.length > 0 ? fetchedPages : ["dashboard"]);
          
          // Also fetch page titles for regular users
          try {
            const settingsResponse = await axios.get(
              `${API}/tenants/my-tenant-settings`,
              { params: { 
                Authorization: `Bearer ${sessionToken}`, 
                "X-Tenant-ID": currentTenantId,
                _t: Date.now() 
              } }
            );
            // Check both paths for page_titles (display_settings.page_titles and direct page_titles)
            const fetchedPageTitles = settingsResponse.data?.display_settings?.page_titles 
              || settingsResponse.data?.page_titles 
              || {};
            console.log("Fetched page titles for regular user:", fetchedPageTitles);
            setLocalPageTitles(fetchedPageTitles);
          } catch (settingsErr) {
            console.log("Could not fetch page titles:", settingsErr);
          }
        }
      } catch (error) {
        console.error("Error fetching visible pages:", error);
        // On error, show only core pages (conservative fallback)
        const defaultPages = [
          "dashboard", "website-traffic", "social-insights",
          "form-submissions", "mqls", "sqls", "deals"
        ];
        setVisiblePages(defaultPages);
      }
    };
    
    fetchVisiblePages();
  }, [sessionToken, isSuperAdmin, tenantChangeCount]);  // Re-run when tenant changes

  // Check if GSC is connected for the current tenant
  useEffect(() => {
    const checkGSCStatus = async () => {
      if (!sessionToken) return;
      
      const currentTenantId = sessionStorage.getItem("viewing_tenant_id");
      if (!currentTenantId) {
        setGscConnected(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/gsc/status`, {
          params: { 
            Authorization: `Bearer ${sessionToken}`,
            "X-Tenant-ID": currentTenantId
          }
        });
        setGscConnected(response.data?.connected === true);
      } catch (err) {
        console.log("GSC status check failed:", err);
        setGscConnected(false);
      }
    };
    
    checkGSCStatus();
  }, [sessionToken, viewingTenantId]);
  
  // Get custom page title or default
  const getPageTitle = (pageId, defaultTitle) => {
    const titleMap = {
      "mqls": pageTitles.mqls,
      "sqls": pageTitles.sqls,
      "deals": pageTitles.deals,
      "form-submissions": pageTitles.form_submissions,
      "website-traffic": pageTitles.website_traffic,
      "for-marketing-insights": pageTitles.for_marketing_insights,
      "social-insights": pageTitles.social_insights,
      "dashboard": pageTitles.dashboard,
      "anomaly-detection": "Anomaly Detection",
      "ai-chat": "AI Chat",
      "gsc-tracking": "Keyword Rankings"
    };
    return titleMap[pageId] || defaultTitle;
  };

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close sidebar when navigating on mobile
  const handleNavClick = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };
  
  // Filter nav items based on visibility settings
  // When viewing a tenant (including super admin impersonation), respect that tenant's page visibility
  const baseFilteredNavItems = visiblePages 
    ? navItems.filter(item => {
        if (item.adminOnly && !effectiveIsAdmin) return false;
        return visiblePages.includes(item.pageId);
      })
    : navItems.filter(item => !item.adminOnly || effectiveIsAdmin); // Default while loading

  // Add GSC nav item dynamically if GSC is connected for this tenant AND it's in visible pages
  const gscPageVisible = !visiblePages || visiblePages.includes("gsc-tracking");
  const filteredNavItems = gscConnected && gscPageVisible
    ? [
        ...baseFilteredNavItems,
        { to: "/app/goals/gsc-tracking", icon: Search, label: "Keyword Rankings", pageId: "gsc-tracking" }
      ]
    : baseFilteredNavItems;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={`flex min-h-screen bg-slate-50 ${impersonating ? 'pt-12' : ''}`}>
      {/* Mobile Header with Hamburger */}
      {isMobile && (
        <div className={`fixed ${impersonating ? 'top-12' : 'top-0'} left-0 right-0 z-[60] bg-slate-900 px-4 py-3 flex items-center justify-between h-14`}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:bg-slate-800 p-2 rounded-lg transition-colors active:bg-slate-700 touch-manipulation"
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
              type="button"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
              JCI Analytics
            </h1>
          </div>
          {/* Profile Dropdown with Sign Out */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="focus:outline-none" data-testid="mobile-profile-btn">
                <Avatar className="h-8 w-8 cursor-pointer hover:ring-2 hover:ring-[#00A5A8] transition-all">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#00A5A8] text-white text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-600 focus:text-red-600 cursor-pointer"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[45]"
          style={{ top: "56px" }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className="fixed left-0 w-64 bg-slate-900 text-slate-300 z-[50] flex flex-col transition-transform duration-300 ease-in-out"
        style={{
          top: isMobile ? (impersonating ? "104px" : "56px") : (impersonating ? "48px" : "0"),
          height: isMobile ? (impersonating ? "calc(100vh - 104px)" : "calc(100vh - 56px)") : (impersonating ? "calc(100vh - 48px)" : "100vh"),
          transform: isMobile ? (sidebarOpen ? "translateX(0)" : "translateX(-100%)") : "translateX(0)",
        }}
        data-testid="sidebar"
      >
        {/* Logo Section */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <img 
              src={JMAP_LOGO_URL} 
              alt="j-MAP" 
              className="h-10 w-10 rounded-lg object-cover"
            />
            <div>
              <h1 className="text-lg font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
                j-MAP
              </h1>
              <p className="text-xs text-slate-500">by JCI Marketing</p>
            </div>
          </div>
          {/* Instance/Platform Name - shows tenant's custom name */}
          {platformName && (
            <div className="mt-3 pt-3 border-t border-slate-800">
              <p className="text-sm font-semibold text-slate-300">{platformName}</p>
            </div>
          )}
        </div>

        {/* Presentation Mode Active Indicator */}
        {isPresentationMode && (
          <div className="px-3 py-2 mx-2 mt-2 bg-emerald-500/20 border border-emerald-500/40 rounded-lg">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <EyeOff className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs text-emerald-300 font-medium">
                  Presentation Mode
                </span>
              </div>
              <button
                onClick={togglePresentationMode}
                className="text-emerald-400 hover:text-emerald-200 text-xs font-medium px-1.5 py-0.5 hover:bg-emerald-500/20 rounded"
                data-testid="exit-presentation-mode"
              >
                Exit
              </button>
            </div>
          </div>
        )}

        {/* Tenant Viewing Indicator (compact, in sidebar) */}
        {isSuperAdmin && isViewingNonHomeTenant && (
          <div className="px-3 py-2 mx-2 mt-2 bg-amber-500/20 border border-amber-500/40 rounded-lg">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <Building className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                <span className="text-xs text-amber-300 truncate font-medium">
                  {viewingTenantName}
                </span>
              </div>
              <button
                onClick={exitTenantView}
                className="text-amber-400 hover:text-amber-200 text-xs font-medium px-1.5 py-0.5 hover:bg-amber-500/20 rounded flex-shrink-0"
              >
                Exit
              </button>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
          <ul className="space-y-1">
            {filteredNavItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `sidebar-item ${isActive ? "active" : ""}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{getPageTitle(item.pageId, item.label)}</span>
                </NavLink>
              </li>
            ))}
            
            {/* Admin Section - Show for admins OR when impersonating */}
            {effectiveIsAdmin && (
              <>
                <li className="px-4 pt-6 pb-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Admin
                  </span>
                </li>
                {adminNavItems.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        `sidebar-item ${isActive ? "active" : ""}`
                      }
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </>
            )}
            
            {/* Platform Owner Section - JCI Master Admins Only */}
            {isSuperAdmin && (
              <>
                <li className="px-4 pt-6 pb-2">
                  <span className="text-xs font-semibold text-amber-500 uppercase tracking-wider">
                    Platform Owner
                  </span>
                </li>
                {superAdminNavItems.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        `sidebar-item ${isActive ? "active bg-amber-900/20" : ""}`
                      }
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <item.icon className="w-5 h-5 text-amber-500" />
                      <span className="text-amber-400">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </>
            )}
          </ul>
        </nav>

        {/* User Section */}
        <div className="border-t border-slate-800 p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#00A5A8] text-white text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {user?.email}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                {user?.role && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#00A5A8]/10 text-[#00A5A8] mt-1">
                    {user.role.replace("_", " ")}
                  </span>
                )}
              </div>
              <DropdownMenuSeparator />
              {/* Presentation Mode Toggle */}
              <div className="px-2 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isPresentationMode ? (
                      <EyeOff className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Eye className="w-4 h-4 text-slate-500" />
                    )}
                    <span className="text-sm">Presentation Mode</span>
                  </div>
                  <Switch
                    checked={isPresentationMode}
                    onCheckedChange={togglePresentationMode}
                    data-testid="presentation-mode-toggle"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1 pl-6">
                  {isPresentationMode ? "Data is masked" : "Showing real data"}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className="main-content lg:ml-64" 
        style={{
          paddingTop: isMobile ? (impersonating ? "7rem" : "4rem") : (impersonating ? "3rem" : "0")
        }}
        data-testid="main-content"
      >
        <div className="max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default SidebarLayout;
